import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LowPriorityIcon from '@mui/icons-material/LowPriority';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import CustomPagination from '../../components/forms/Pagination';

const QuickFilterDivider = () => (
  <div style={{ width: 1, height: 24, backgroundColor: '#E0E0E0', margin: '0 8px' }} />
);
import {
  TasksClient,
  TaskSummaryDto,
  TaskPrioritiesClient,
  TaskPriorityDto,
  TaskStatusesClient,
  TaskStatusDto,
  TaskCategoriesClient,
  TaskCategoryDto,
} from '../../app/services/api/tasksClient';

const tasksClient = TasksClient;
const prioritiesClient = TaskPrioritiesClient;
const statusesClient = TaskStatusesClient;
const categoriesClient = TaskCategoriesClient;

const TasksPage: React.FC = () => {
  const { t } = useTranslation('tasks');
  const navigate = useNavigate();
  const { hasPermissionTo, selectedStore } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['AdvancedTasksView']) : false;
  const canCreate = hasPermissionTo ? hasPermissionTo(['AdvancedTasksCreate']) : false;
  const canReassign = hasPermissionTo ? hasPermissionTo(['AdvancedTasksReassign']) : false;
  const canReprioritise = hasPermissionTo ? hasPermissionTo(['AdvancedTasksRePrioritise']) : false;
  const canDelete = hasPermissionTo ? hasPermissionTo(['AdvancedTasksDelete']) : false;
  const canBulkReassign = hasPermissionTo ? hasPermissionTo(['AdvancedTasksBulkReassign']) : false;
  const canBulkReprioritise = hasPermissionTo ? hasPermissionTo(['AdvancedTasksBulkReprioritise']) : false;

  const [tasks, setTasks] = useState<TaskSummaryDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);
  const [statuses, setStatuses] = useState<TaskStatusDto[]>([]);
  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);

  // My vs Store task filter chips (both selected by default)
  const [showMyTasks, setShowMyTasks] = useState<boolean>(true);
  const [showStoreTasks, setShowStoreTasks] = useState<boolean>(true);

  const [quickFilter, setQuickFilter] = useState<'today' | 'past' | 'overdue' | 'upcoming' | ''>('');
  const [statusChip, setStatusChip] = useState<'notStarted' | 'inProgress' | 'completed' | 'outstanding' | 'pendingApproval' | ''>('');
  const [urgentChip, setUrgentChip] = useState<boolean>(false);
  const [filterStatusId, setFilterStatusId] = useState<number | ''>('');
  const [filterPriorityId, setFilterPriorityId] = useState<number | ''>('');
  const [filterCategoryId, setFilterCategoryId] = useState<number | ''>('');
  const [filterTargetType, setFilterTargetType] = useState<string>('');
  const [filterStore, setFilterStore] = useState<string>('');
  const [filterColleague, setFilterColleague] = useState<string>('');
  const [filterPersona, setFilterPersona] = useState<string>('');
  const [filterFromDate, setFilterFromDate] = useState<string>('');
  const [filterToDate, setFilterToDate] = useState<string>('');

  const [isMoreFiltersOpen, setIsMoreFiltersOpen] = useState(false);

  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskIdToDelete, setTaskIdToDelete] = useState<number | null>(null);

  const fakePersonas: { id: number; name: string }[] = [
    { id: 1, name: 'Store Manager' },
    { id: 2, name: 'Assistant Manager' },
    { id: 3, name: 'Team Member' },
  ];

  const fakeUsers: { id: number; name: string }[] = [
    { id: 1001, name: 'Alex Smith' },
    { id: 1002, name: 'Jamie Lee' },
    { id: 1003, name: 'Taylor Brown' },
  ];

  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [bulkReassignDialogOpen, setBulkReassignDialogOpen] = useState(false);
  const [reassignMode, setReassignMode] = useState<'ROLE' | 'USER'>('ROLE');
  const [reassignPersonaId, setReassignPersonaId] = useState<number | ''>('');
  const [reassignUserId, setReassignUserId] = useState<number | ''>('');
  const [taskIdToReassign, setTaskIdToReassign] = useState<number | null>(null);

  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [bulkPriorityDialogOpen, setBulkPriorityDialogOpen] = useState(false);
  const [priorityTaskId, setPriorityTaskId] = useState<number | null>(null);
  const [newPriorityId, setNewPriorityId] = useState<number | ''>('');
  const [bulkPriorityMode, setBulkPriorityMode] = useState<'INCREASE_ONE' | 'RESET_TEMPLATE'>('INCREASE_ONE');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!canView) return;

    prioritiesClient.getPriorities().then(setPriorities).catch(() => setPriorities([]));
    statusesClient.getStatuses().then(setStatuses).catch(() => setStatuses([]));
    categoriesClient.getCategories().then(setCategories).catch(() => setCategories([]));
  }, [canView]);

  useEffect(() => {
    if (!statuses.length) return;

    if (!statusChip) {
      setFilterStatusId('');
      return;
    }

    const map: Record<string, string> = {
      notStarted: 'Not Started',
      inProgress: 'In Progress',
      completed: 'Completed',
      outstanding: '', // handled via composite logic, not direct status id
      pendingApproval: 'Pending Approval',
    };

    const targetName = map[statusChip];
    if (!targetName) {
      setFilterStatusId('');
      return;
    }
    const status = statuses.find(s => s.name === targetName);
    setFilterStatusId(status ? status.id : '');
  }, [statusChip, statuses]);

  useEffect(() => {
    if (!priorities.length) return;

    if (!urgentChip) {
      setFilterPriorityId('');
      return;
    }

    const high = priorities.find(p => p.name === 'High');
    setFilterPriorityId(high ? high.id : '');
  }, [urgentChip, priorities]);

  useEffect(() => {
    if (!canView) return;

    // Use selected store from context
    const storeNumber = selectedStore?.storeNumber;
    const franchiseName = selectedStore?.franchiseName || 'Demo Franchise'; // Required by API

    // Don't fetch if no store is selected
    if (!storeNumber) {
      setTasks([]);
      return;
    }

    let from: Date | undefined;
    let to: Date | undefined;

    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(startOfToday);
    endOfToday.setDate(endOfToday.getDate() + 1);

    if (quickFilter === 'today') {
      from = startOfToday;
      to = endOfToday;
      setFilterFromDate(startOfToday.toISOString().substring(0, 10));
      setFilterToDate(startOfToday.toISOString().substring(0, 10));
    } else if (quickFilter === 'past') {
      from = undefined;
      to = startOfToday;
      setFilterFromDate('');
      setFilterToDate(startOfToday.toISOString().substring(0, 10));
    } else if (quickFilter === 'upcoming') {
      from = endOfToday;
      to = new Date(endOfToday);
      to.setDate(to.getDate() + 7);
      setFilterFromDate(endOfToday.toISOString().substring(0, 10));
      setFilterToDate(to.toISOString().substring(0, 10));
    } else if (filterFromDate || filterToDate) {
      from = filterFromDate ? new Date(filterFromDate) : undefined;
      to = filterToDate ? new Date(filterToDate) : undefined;
    }

    tasksClient
      .getStoreTasks(storeNumber, franchiseName, from, to)
      .then(setTasks)
      .catch(() => setTasks([]));
  }, [canView, quickFilter, filterFromDate, filterToDate, selectedStore?.storeNumber, selectedStore?.franchiseName]);

  const filteredTasks = useMemo(() => {
    // Mock current user ID - in real app this would come from UserContext
    const currentUserId = 1001;

    const filtered = tasks.filter(t => {
      // Apply My Tasks / Store Tasks filter
      // My Tasks = tasks assigned to current user
      // Store Tasks = tasks assigned to others (roles or other users)
      const isMyTask = t.assignedUserId === currentUserId;

      // If both chips are selected, show all tasks
      // If neither is selected, show no tasks
      // If only one is selected, show only those tasks
      if (!showMyTasks && !showStoreTasks) return false;
      if (showMyTasks && !showStoreTasks && !isMyTask) return false;
      if (!showMyTasks && showStoreTasks && isMyTask) return false;

      if (quickFilter === 'overdue' && !t.isOverdue) return false;
      if (statusChip === 'outstanding') {
        if (t.statusId === 6) return false;
        if (t.isOverdue) return false;
      }
      if (filterStatusId && t.statusId !== Number(filterStatusId)) {
        return false;
      }
      if (filterPriorityId && t.priorityId !== Number(filterPriorityId)) {
        return false;
      }
      if (filterCategoryId && t.categoryName) {
        const category = categories.find(c => c.name === t.categoryName);
        if (!category || category.id !== Number(filterCategoryId)) return false;
      }
      if (filterTargetType && t.targetType && t.targetType !== filterTargetType) return false;
      if (filterStore && t.storeNumber !== filterStore) return false;
      if (filterColleague && (!t.assignedUserName || !t.assignedUserName.toLowerCase().includes(filterColleague.toLowerCase()))) return false;
      if (filterPersona && (!t.personaName || !t.personaName.toLowerCase().includes(filterPersona.toLowerCase()))) return false;
      return true;
    });

    // Sort by due date ascending (earliest first), with completed tasks sorted by completion date
    return filtered.sort((a, b) => {
      // Use dueDateUtc for incomplete tasks, completedDateUtc for completed tasks
      const aDate = a.completedDateUtc ? new Date(a.completedDateUtc) : (a.dueDateUtc ? new Date(a.dueDateUtc) : null);
      const bDate = b.completedDateUtc ? new Date(b.completedDateUtc) : (b.dueDateUtc ? new Date(b.dueDateUtc) : null);

      // Tasks without dates go to the end
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;

      return aDate.getTime() - bDate.getTime();
    });
  }, [tasks, showMyTasks, showStoreTasks, quickFilter, filterStatusId, filterPriorityId, filterCategoryId, filterTargetType, filterStore, filterColleague, filterPersona, priorities, statuses, categories, statusChip]);

  const pagedTasks = useMemo(
    () => filteredTasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredTasks, page, rowsPerPage],
  );

  const toggleSelected = (id: number) => {
    setSelectedTaskIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  };

  const allSelectedOnPage = pagedTasks.length > 0 && pagedTasks.every(t => selectedTaskIds.includes(t.id));
  const toggleSelectAll = () => {
    if (allSelectedOnPage) {
      setSelectedTaskIds(prev => prev.filter(id => !pagedTasks.some(t => t.id === id)));
    } else {
      setSelectedTaskIds(prev => Array.from(new Set([...prev, ...pagedTasks.map(t => t.id)])));
    }
  };

  const handleRequestDelete = (id: number) => {
    setTaskIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (taskIdToDelete == null) {
      setDeleteDialogOpen(false);
      return;
    }
    // For now, just remove from local state; wire to API later if needed.
    setTasks(prev => prev.filter(t => t.id !== taskIdToDelete));
    setSelectedTaskIds(prev => prev.filter(id => id !== taskIdToDelete));
    setDeleteDialogOpen(false);
    setTaskIdToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTaskIdToDelete(null);
  };

  const handleOpenReassign = (taskId: number) => {
    setTaskIdToReassign(taskId);
    setReassignMode('ROLE');
    setReassignPersonaId('');
    setReassignUserId('');
    setReassignDialogOpen(true);
  };

  const handleOpenBulkReassign = () => {
    if (!selectedTaskIds.length) return;
    setTaskIdToReassign(null);
    setReassignMode('ROLE');
    setReassignPersonaId('');
    setReassignUserId('');
    setBulkReassignDialogOpen(true);
  };

  const applyReassign = (taskIds: number[]) => {
    const personaName =
      reassignMode === 'ROLE' && reassignPersonaId
        ? fakePersonas.find(p => p.id === reassignPersonaId)?.name ?? null
        : null;
    const userName =
      reassignMode === 'USER' && reassignUserId
        ? fakeUsers.find(u => u.id === reassignUserId)?.name ?? null
        : null;

    setTasks(prev =>
      prev.map(t =>
        taskIds.includes(t.id)
          ? {
            ...t,
            personaName: personaName ?? t.personaName ?? undefined,
            assignedUserName: userName ?? t.assignedUserName ?? undefined,
          }
          : t,
      ),
    );
  };

  const handleConfirmReassign = () => {
    if (taskIdToReassign != null) {
      applyReassign([taskIdToReassign]);
      setReassignDialogOpen(false);
    }
  };

  const handleConfirmBulkReassign = () => {
    if (!selectedTaskIds.length) return;
    applyReassign(selectedTaskIds);
    setBulkReassignDialogOpen(false);
  };

  const handleCancelReassign = () => {
    setReassignDialogOpen(false);
    setBulkReassignDialogOpen(false);
  };

  const getTemplatePriorityIdForTask = (task: TaskSummaryDto): number | null => {
    const priority = priorities.find(p => task.defaultPriorityId === p.id);
    return priority ? priority.id : null;
  };

  const handleOpenPriority = (taskId: number) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;
    const templatePriorityId = getTemplatePriorityIdForTask(task);
    if (!templatePriorityId) return;

    setPriorityTaskId(taskId);

    const allowed = priorities.filter(p => p.id >= templatePriorityId);
    const current = priorities.find(p => task.priorityId === p.id);
    const initial =
      current && current.id >= templatePriorityId
        ? current.id
        : allowed.length
          ? allowed[0].id
          : templatePriorityId;
    setNewPriorityId(initial);
    setPriorityDialogOpen(true);
  };

  const handleConfirmPriority = () => {
    if (priorityTaskId == null || newPriorityId === '') return;
    const chosen = priorities.find(p => p.id === newPriorityId);
    if (!chosen) return;
    const task = tasks.find(t => t.id === priorityTaskId);
    if (!task) return;
    const templatePriorityId = getTemplatePriorityIdForTask(task);
    if (!templatePriorityId || chosen.id < templatePriorityId) return;

    setTasks(prev => prev.map(t => (t.id === priorityTaskId ? { ...t, priority: chosen.name } : t)));
    setPriorityDialogOpen(false);
  };

  const handleCancelPriority = () => {
    setPriorityDialogOpen(false);
  };

  const handleOpenBulkPriority = () => {
    if (!selectedTaskIds.length) return;
    setBulkPriorityMode('INCREASE_ONE');
    setBulkPriorityDialogOpen(true);
  };

  const handleConfirmBulkPriority = () => {
    if (!selectedTaskIds.length) return;

    if (bulkPriorityMode === 'INCREASE_ONE') {
      setTasks(prev => {
        return prev.map(t => {
          if (!selectedTaskIds.includes(t.id)) return t;
          const current = priorities.find(p => t.priorityId === p.id);
          if (!current) return t;

          // Sort priorities by ID ascending (higher ID = higher priority)
          const sortedPriorities = [...priorities].sort((a, b) => a.id - b.id);
          const currentIndex = sortedPriorities.findIndex(p => p.id === current.id);

          // Move to a higher priority (higher index), but don't exceed max
          const nextIndex = Math.min(sortedPriorities.length - 1, currentIndex + 1);
          const next = sortedPriorities[nextIndex];

          return next ? { ...t, priorityId: next.id } : t;
        });
      });
    } else {
      setTasks(prev => {
        return prev.map(t => {
          if (!selectedTaskIds.includes(t.id)) return t;
          const templatePriorityId = getTemplatePriorityIdForTask(t);
          const templatePriority = templatePriorityId
            ? priorities.find(p => p.id === templatePriorityId)
            : undefined;
          return templatePriority ? { ...t, priorityId: templatePriorityId || 2 } : t;
        });
      });
    }

    setBulkPriorityDialogOpen(false);
  };

  const handleCancelBulkPriority = () => {
    setBulkPriorityDialogOpen(false);
  };

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>{t('list.title', 'Tasks')}</PageTitle>
        <Pill text={t('list.noPermission', 'You do not have permission to view tasks.')} />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>{t('list.title', 'Tasks')}</PageTitle>
          <Grid container
            spacing={2}
            justifyContent='flex-end'
            alignItems='center'
            sx={{ width: 'auto' }}>
            {canCreate && (
              <Grid item>
                <Button
                  size='lg'
                  variant='primary'
                  startIcon={<Plus />}
                  onClick={() => navigate('/tasks/create')}
                >
                  {t('list.actionCreateAdhocTask', 'Create Ad-hoc Task')}
                </Button>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={12}>
          {/* Row 1: quick time chips + status/priority chips on a single line */}
          <Grid container
            spacing={2}
            alignItems='center'>
            {/* My Tasks / Store Tasks filter group */}
            <Grid item>
              <Grid container
                spacing={1}
                alignItems='center'>
                <Grid item>
                  <Chip
                    label={t('list.chipMyTasks', 'My Tasks')}
                    color={showMyTasks ? 'primary' : 'default'}
                    onClick={() => setShowMyTasks(v => !v)}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={t('list.chipStoreTasks', 'Store Tasks')}
                    color={showStoreTasks ? 'primary' : 'default'}
                    onClick={() => setShowStoreTasks(v => !v)}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Divider between chip groups */}
            <Grid item>
              <QuickFilterDivider />
            </Grid>

            {/* Time group: Today, Past, Upcoming */}
            <Grid item>
              <Grid container
                spacing={1}
                alignItems='center'>
                <Grid item>
                  <Chip
                    label={t('list.chipToday', 'Today')}
                    color={quickFilter === 'today' ? 'primary' : 'default'}
                    onClick={() => setQuickFilter(prev => (prev === 'today' ? '' : 'today'))}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={t('list.chipPast', 'Past')}
                    color={quickFilter === 'past' ? 'primary' : 'default'}
                    onClick={() => setQuickFilter(prev => (prev === 'past' ? '' : 'past'))}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={t('list.chipOverdue', 'Overdue')}
                    color={quickFilter === 'overdue' ? 'primary' : 'default'}
                    onClick={() => setQuickFilter(prev => (prev === 'overdue' ? '' : 'overdue'))}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={t('list.chipUpcoming', 'Upcoming')}
                    color={quickFilter === 'upcoming' ? 'primary' : 'default'}
                    onClick={() => setQuickFilter(prev => (prev === 'upcoming' ? '' : 'upcoming'))}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Divider between chip groups */}
            <Grid item>
              <QuickFilterDivider />
            </Grid>

            {/* Urgent / Escalated group */}
            <Grid item>
              <Grid container
                spacing={1}
                alignItems='center'>
                <Grid item>
                  <Chip
                    label={t('list.chipUrgent', 'Urgent')}
                    color={urgentChip ? 'primary' : 'default'}
                    onClick={() => setUrgentChip(v => !v)}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={t('list.chipEscalated', 'Escalated')}
                    color={filterTargetType === 'Team' ? 'primary' : 'default'}
                    onClick={() => setFilterTargetType(prev => (prev === 'Team' ? '' : 'Team'))}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Divider between chip groups */}
            <Grid item>
              <QuickFilterDivider />
            </Grid>

            {/* Status group: Not Started, In Progress, Completed, Outstanding */}
            <Grid item>
              <Grid container
                spacing={1}
                alignItems='center'>
                <Grid item>
                  <Chip
                    label={t('list.chipOutstanding', 'Outstanding')}
                    color={statusChip === 'outstanding' ? 'primary' : 'default'}
                    onClick={() => setStatusChip(prev => (prev === 'outstanding' ? '' : 'outstanding'))}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={t('list.chipPendingApproval', 'Pending Approval')}
                    color={statusChip === 'pendingApproval' ? 'primary' : 'default'}
                    onClick={() => setStatusChip(prev => (prev === 'pendingApproval' ? '' : 'pendingApproval'))}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={t('list.chipCompleted', 'Completed')}
                    color={statusChip === 'completed' ? 'primary' : 'default'}
                    onClick={() => setStatusChip(prev => (prev === 'completed' ? '' : 'completed'))}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Row 2: advanced filters accordion */}
        <Grid item
          xs={12}>
          <Accordion
            variant='borderless'
            expanded={isMoreFiltersOpen}
            onChange={(_, expanded) => setIsMoreFiltersOpen(expanded)}
          >
            <AccordionSummary>
              <Grid container
                alignItems='center'
                justifyContent='space-between'
                sx={{ width: '100%' }}>
                <Grid item>
                  <Grid container
                    spacing={1}
                    alignItems='center'>
                    <Grid item>
                      {t('list.advancedFiltersTitle', 'Advanced Filters')}
                    </Grid>
                    <Grid item
                      style={{ marginTop: '8px' }}>
                      {isMoreFiltersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Button
                    size='md'
                    variant='secondary'
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent accordion toggle
                      // Reset to default state
                      setShowMyTasks(true);
                      setShowStoreTasks(true);
                      setQuickFilter('');
                      setStatusChip('');
                      setUrgentChip(false);
                      setFilterFromDate('');
                      setFilterToDate('');
                      setFilterCategoryId('');
                      setFilterPriorityId('');
                      setFilterStatusId('');
                      setFilterPersona('');
                      setFilterColleague('');
                      setFilterStore('');
                      setFilterTargetType('');
                    }}
                  >
                    {t('list.clearFilters', 'Clear Filters')}
                  </Button>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container
                spacing={3}>
                {/* Date Range Section */}
                <Grid item
                  xs={12}>
                  <Typography variant='textSM'
                    sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                    {t('list.sectionDateRange', 'Date Range')}
                  </Typography>
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    label={t('list.filterFrom', 'From')}
                    type='date'
                    fullWidth
                    size='small'
                    InputLabelProps={{ shrink: true }}
                    value={filterFromDate}
                    onChange={e => {
                      setFilterFromDate(e.target.value);
                      setQuickFilter(''); // Clear quick filter when using custom dates
                    }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    label={t('list.filterTo', 'To')}
                    type='date'
                    fullWidth
                    size='small'
                    InputLabelProps={{ shrink: true }}
                    value={filterToDate}
                    onChange={e => {
                      setFilterToDate(e.target.value);
                      setQuickFilter(''); // Clear quick filter when using custom dates
                    }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  md={6} />

                {/* Task Properties Section */}
                <Grid item
                  xs={12}>
                  <Typography variant='textSM'
                    sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                    {t('list.sectionTaskProperties', 'Task Properties')}
                  </Typography>
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    select
                    label={t('list.filterCategory', 'Category')}
                    fullWidth
                    size='small'
                    value={filterCategoryId}
                    onChange={e => setFilterCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
                  >
                    <MenuItem value=''>{t('list.filterAllCategories', 'All categories')}</MenuItem>
                    {categories.map(c => (
                      <MenuItem key={c.id}
                        value={c.id}>{c.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    select
                    label={t('list.filterPriority', 'Priority')}
                    fullWidth
                    size='small'
                    value={filterPriorityId}
                    onChange={e => {
                      setFilterPriorityId(e.target.value === '' ? '' : Number(e.target.value));
                      setUrgentChip(false); // Clear urgent chip when using dropdown
                    }}
                  >
                    <MenuItem value=''>{t('list.filterAllPriorities', 'All priorities')}</MenuItem>
                    {priorities.map(p => (
                      <MenuItem key={p.id}
                        value={p.id}>{p.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    select
                    label={t('list.filterStatus', 'Status')}
                    fullWidth
                    size='small'
                    value={filterStatusId}
                    onChange={e => {
                      setFilterStatusId(e.target.value === '' ? '' : Number(e.target.value));
                      setStatusChip(''); // Clear status chip when using dropdown
                    }}
                  >
                    <MenuItem value=''>{t('list.filterAllStatuses', 'All statuses')}</MenuItem>
                    {statuses.map(s => (
                      <MenuItem key={s.id}
                        value={s.id}>{s.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item
                  xs={12}
                  md={3} />

                {/* Assignment Section */}
                <Grid item
                  xs={12}>
                  <Typography variant='textSM'
                    sx={{ fontWeight: 600, color: 'text.secondary', mb: 1 }}>
                    {t('list.sectionAssignment', 'Assignment')}
                  </Typography>
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    select
                    label={t('list.filterRole', 'Role')}
                    fullWidth
                    size='small'
                    value={filterPersona}
                    onChange={e => setFilterPersona(e.target.value)}
                  >
                    <MenuItem value=''>{t('list.filterAllRoles', 'All roles')}</MenuItem>
                    {fakePersonas.map(p => (
                      <MenuItem key={p.id}
                        value={p.name}>{p.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    select
                    label={t('list.filterAssignedTo', 'Assigned To')}
                    fullWidth
                    size='small'
                    value={filterColleague}
                    onChange={e => setFilterColleague(e.target.value)}
                  >
                    <MenuItem value=''>{t('list.filterAllColleagues', 'All colleagues')}</MenuItem>
                    {fakeUsers.map(u => (
                      <MenuItem key={u.id}
                        value={u.name}>{u.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    select
                    label={t('list.filterStore', 'Store')}
                    fullWidth
                    size='small'
                    value={filterStore}
                    onChange={e => setFilterStore(e.target.value)}
                  >
                    <MenuItem value=''>{t('list.filterAllStores', 'All stores')}</MenuItem>
                    <MenuItem value='1002804'>{t('list.storeOption', 'Store {{number}}', { number: '1002804' })}</MenuItem>
                    <MenuItem value='1002805'>{t('list.storeOption', 'Store {{number}}', { number: '1002805' })}</MenuItem>
                    <MenuItem value='1002806'>{t('list.storeOption', 'Store {{number}}', { number: '1002806' })}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item
                  xs={12}
                  md={3}>
                  <TextField
                    select
                    label={t('list.filterTargetType', 'Target Type')}
                    fullWidth
                    size='small'
                    value={filterTargetType}
                    onChange={e => setFilterTargetType(e.target.value)}
                  >
                    <MenuItem value=''>{t('list.filterAllTypes', 'All types')}</MenuItem>
                    <MenuItem value='Team'>{t('list.targetTypeTeam', 'Team')}</MenuItem>
                    <MenuItem value='Location'>{t('list.targetTypeLocation', 'Location')}</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Bulk actions moved up beside Create Ad-hoc Task */}

        <Grid item
          xs={12}>
          <Section>
            <Grid container
              alignItems='center'
              justifyContent='space-between'
              sx={{ mb: 2 }}>
              <Grid item>
                <TableHeader>
                  {t('list.tableTitle', 'Tasks')}
                  <Pill text={t('list.itemCount', '{{count}} Tasks', { count: filteredTasks.length })} />
                </TableHeader>
              </Grid>
              {(canBulkReassign || canBulkReprioritise) && (
                <Grid item>
                  <Grid container
                    spacing={1}
                    justifyContent='flex-end'
                    alignItems='center'>
                    {canBulkReassign && (
                      <Grid item>
                        <Button
                          size='md'
                          variant='secondary'
                          disabled={selectedTaskIds.length === 0}
                          onClick={handleOpenBulkReassign}
                        >
                          {t('list.actionReassign', 'Reassign')}
                        </Button>
                      </Grid>
                    )}
                    {canBulkReprioritise && (
                      <Grid item>
                        <Button
                          size='md'
                          variant='secondary'
                          disabled={selectedTaskIds.length === 0}
                          onClick={handleOpenBulkPriority}
                        >
                          {t('list.actionChangePriority', 'Change Priority')}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allSelectedOnPage}
                            indeterminate={selectedTaskIds.length > 0 && !allSelectedOnPage}
                            onChange={toggleSelectAll}
                          />
                        }
                        label=''
                      />
                    </TableCell>
                    <TableCell>{t('list.columnTitle', 'Title')}</TableCell>
                    <TableCell>{t('list.columnStore', 'Store')}</TableCell>
                    <TableCell>{t('list.columnCategory', 'Category')}</TableCell>
                    <TableCell>{t('list.columnStatus', 'Status')}</TableCell>
                    <TableCell>{t('list.columnPriority', 'Priority')}</TableCell>
                    <TableCell>{t('list.columnDueCompleted', 'Due / Completed')}</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedTasks.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => navigate(`/tasks/${row.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding='checkbox'
                        onClick={e => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedTaskIds.includes(row.id)}
                          onChange={() => toggleSelected(row.id)}
                        />
                      </TableCell>
                      <TableCell>{row.title}</TableCell>
                      <TableCell>
                        <div>{row.storeName || row.storeNumber}</div>
                        <div style={{ fontSize: '0.75rem', color: '#666' }}>{row.storeNumber}</div>
                      </TableCell>
                      <TableCell>
                        {row.categoryName && (
                          <Chip
                            label={row.categoryName}
                            size='small'
                            variant='outlined'
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statuses.find(s => s.id === row.statusId)?.name ?? t('list.unknown', 'Unknown')}
                          size='small'
                          color={
                            row.statusId === 6 ? 'success' :
                              row.statusId === 4 ? 'success' :
                                row.statusId === 5 ? 'error' :
                                  row.statusId === 3 ? 'warning' :
                                    row.statusId === 2 ? 'info' : 'secondary'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={priorities.find(p => p.id === row.priorityId)?.name ?? t('list.unknown', 'Unknown')}
                          size='small'
                          color={
                            (priorities.find(p => p.id === row.priorityId)?.name ?? '').toLowerCase() === 'high' ? 'error' :
                              (priorities.find(p => p.id === row.priorityId)?.name ?? '').toLowerCase() === 'medium' ? 'warning' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {row.statusId === 6 && row.completedDateUtc ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span>{new Date(row.completedDateUtc).toLocaleString()}</span>
                            {row.dueDateUtc && new Date(row.completedDateUtc) > new Date(row.dueDateUtc) && (
                              <WarningAmberIcon
                                fontSize='small'
                                color='warning'
                                titleAccess={t('list.completedAfterDueDate', 'Completed after due date')}
                              />
                            )}
                          </div>
                        ) : (
                          row.dueDateUtc ? new Date(row.dueDateUtc).toLocaleString() : ''
                        )}
                      </TableCell>
                      <TableCell
                        align='right'
                        onClick={e => e.stopPropagation()}>
                        {canReassign && (
                          <SwapHorizIcon
                            fontSize='small'
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenReassign(row.id);
                            }}
                            style={{ cursor: 'pointer', marginRight: 8 }}
                          />
                        )}
                        {canReprioritise && (
                          <LowPriorityIcon
                            fontSize='small'
                            onClick={e => {
                              e.stopPropagation();
                              handleOpenPriority(row.id);
                            }}
                            style={{ cursor: 'pointer', marginRight: 8 }}
                          />
                        )}
                        {canDelete && (
                          <DeleteOutlineIcon
                            fontSize='small'
                            color='error'
                            onClick={e => {
                              e.stopPropagation();
                              handleRequestDelete(row.id);
                            }}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedTasks.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8}
                        style={{ textAlign: 'center' }}>
                        {t('list.emptyState', 'No tasks found.')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomPagination
              page={page}
              setPage={setPage}
              maxPages={Math.ceil(filteredTasks.length / rowsPerPage)}
              breakpointForChangeDisplay={120000}
            />
          </Section>
        </Grid>
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCancelDelete}
        >
          <DialogTitle>{t('list.deleteDialogTitle', 'Delete task')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('list.deleteDialogBody', 'Are you sure you want to delete this task?')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCancelDelete}
            >
              {t('list.actionCancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              color='error'
            >
              {t('list.actionDelete', 'Delete')}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={reassignDialogOpen}
          onClose={handleCancelReassign}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>{t('list.reassignDialogTitle', 'Reassign task')}</DialogTitle>
          <DialogContent>
            <Grid container
              spacing={3}
              marginTop={1}>
              <Grid item
                xs={12}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('list.reassignRoleLabel', 'Assign to role')}
                  helperText={t('list.reassignRoleHelper', 'Select a role to assign this task to.')}
                  value={reassignPersonaId}
                  onChange={e => {
                    setReassignPersonaId(e.target.value === '' ? '' : Number(e.target.value));
                    if (e.target.value !== '') setReassignUserId('');
                  }}>
                  <MenuItem value=''>{t('list.reassignNoRoleSelected', 'No role selected')}</MenuItem>
                  {fakePersonas.map(p => (
                    <MenuItem key={p.id}
                      value={p.id}>{p.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('list.reassignUserLabel', 'Or assign to specific user')}
                  helperText={t('list.reassignUserHelper', 'Select a specific user to assign this task to.')}
                  value={reassignUserId}
                  onChange={e => {
                    setReassignUserId(e.target.value === '' ? '' : Number(e.target.value));
                    if (e.target.value !== '') setReassignPersonaId('');
                  }}>
                  <MenuItem value=''>{t('list.reassignNoUserSelected', 'No user selected')}</MenuItem>
                  {fakeUsers.map(u => (
                    <MenuItem key={u.id}
                      value={u.id}>{u.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              variant='secondary'
              onClick={handleCancelReassign}>{t('list.actionCancel', 'Cancel')}</Button>
            <Button
              variant='primary'
              disabled={!reassignPersonaId && !reassignUserId}
              onClick={handleConfirmReassign}>{t('list.actionSave', 'Save')}</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={bulkReassignDialogOpen}
          onClose={handleCancelReassign}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>{t('list.bulkReassignDialogTitle', 'Bulk reassign tasks')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('list.bulkReassignDialogBody', 'Reassign {{count}} selected task{{plural}} to a role or specific user.', { count: selectedTaskIds.length, plural: selectedTaskIds.length !== 1 ? 's' : '' })}
            </DialogContentText>
            <Grid container
              spacing={3}
              marginTop={1}>
              <Grid item
                xs={12}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('list.reassignRoleLabel', 'Assign to role')}
                  helperText={t('list.bulkReassignRoleHelper', 'Select a role to assign all selected tasks to.')}
                  value={reassignPersonaId}
                  onChange={e => {
                    setReassignPersonaId(e.target.value === '' ? '' : Number(e.target.value));
                    if (e.target.value !== '') setReassignUserId('');
                  }}>
                  <MenuItem value=''>{t('list.reassignNoRoleSelected', 'No role selected')}</MenuItem>
                  {fakePersonas.map(p => (
                    <MenuItem key={p.id}
                      value={p.id}>{p.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item
                xs={12}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label={t('list.reassignUserLabel', 'Or assign to specific user')}
                  helperText={t('list.bulkReassignUserHelper', 'Select a specific user to assign all selected tasks to.')}
                  value={reassignUserId}
                  onChange={e => {
                    setReassignUserId(e.target.value === '' ? '' : Number(e.target.value));
                    if (e.target.value !== '') setReassignPersonaId('');
                  }}>
                  <MenuItem value=''>{t('list.reassignNoUserSelected', 'No user selected')}</MenuItem>
                  {fakeUsers.map(u => (
                    <MenuItem key={u.id}
                      value={u.id}>{u.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              variant='secondary'
              onClick={handleCancelReassign}>{t('list.actionCancel', 'Cancel')}</Button>
            <Button
              variant='primary'
              disabled={!reassignPersonaId && !reassignUserId}
              onClick={handleConfirmBulkReassign}>{t('list.actionSave', 'Save')}</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={priorityDialogOpen}
          onClose={handleCancelPriority}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>{t('list.priorityDialogTitle', 'Change task priority')}</DialogTitle>
          <DialogContent>
            <TextField
              select
              fullWidth
              size='small'
              label={t('list.priorityDialogNewPriorityLabel', 'New priority')}
              value={newPriorityId}
              onChange={e => setNewPriorityId(e.target.value === '' ? '' : Number(e.target.value))}>
              {priorities
                .filter(p => {
                  if (priorityTaskId == null) return false;
                  const task = tasks.find(t => t.id === priorityTaskId);
                  if (!task) return false;
                  const templatePriorityId = getTemplatePriorityIdForTask(task);
                  return templatePriorityId ? p.id >= templatePriorityId : true;
                })
                .map(p => (
                  <MenuItem key={p.id}
                    value={p.id}>{p.name}</MenuItem>
                ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button
              variant='secondary'
              onClick={handleCancelPriority}>{t('list.actionCancel', 'Cancel')}</Button>
            <Button
              variant='primary'
              onClick={handleConfirmPriority}>{t('list.actionSave', 'Save')}</Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={bulkPriorityDialogOpen}
          onClose={handleCancelBulkPriority}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>{t('list.bulkPriorityDialogTitle', 'Bulk change priority')}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              {t('list.bulkPriorityDialogBody', 'Change priority for {{count}} selected task{{plural}}.', { count: selectedTaskIds.length, plural: selectedTaskIds.length !== 1 ? 's' : '' })}
            </DialogContentText>
            <RadioGroup
              value={bulkPriorityMode}
              onChange={e => setBulkPriorityMode(e.target.value as 'INCREASE_ONE' | 'RESET_TEMPLATE')}>
              <FormControlLabel
                value='INCREASE_ONE'
                control={<Radio size='small' />}
                label={t('list.bulkPriorityIncreaseLabel', 'Increase priority by one level (e.g. Medium → High)')}
              />
              <FormControlLabel
                value='RESET_TEMPLATE'
                control={<Radio size='small' />}
                label={t('list.bulkPriorityResetLabel', 'Reset to template default priority')}
              />
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <Button
              variant='secondary'
              onClick={handleCancelBulkPriority}>{t('list.actionCancel', 'Cancel')}</Button>
            <Button
              variant='primary'
              onClick={handleConfirmBulkPriority}>{t('list.actionSave', 'Save')}</Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </PageArea>
  );
};

export default TasksPage;
